import { useEffect, useState, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { useDispatch } from 'react-redux';
import { X, Sparkles, TrendingUp, Compass, AlertTriangle, Loader2, Building2 } from 'lucide-react';
import { fetchCompanyInsights } from '../../redux/HSignalSlice/hsignalSlice';

/**
 * AI Company Insights — company research, generated once and shared.
 *
 * Every section here is about the COMPANY, so the whole modal is served from a
 * single per-company cache: the first reader pays for the research and everyone
 * after them reads it free.
 *
 * "How to position yourself" used to sit between them. It is about the reader
 * rather than the company, so it could not share that cache — it was a second
 * AI call charged to every user on open, whether or not they scrolled to it. It
 * now lives behind a button on the company page, next to the fit evidence it
 * argues from, and is requested only when asked for.
 */
const CompanyInsightsModal = ({ open, onClose, companyName, organizationId = null, companyId = null, ticker = null }) => {
    const dispatch = useDispatch();
    const [status, setStatus] = useState('idle'); // idle | loading | done | error
    const [data, setData] = useState(null);
    const [error, setError] = useState(null);

    const load = useCallback(async (refresh = false) => {
        if (!companyName) return;
        setStatus('loading');
        setError(null);
        const result = await dispatch(fetchCompanyInsights({ companyName, organizationId, companyId, ticker, refresh }));
        if (fetchCompanyInsights.fulfilled.match(result)) {
            setData(result.payload);
            setStatus('done');
        } else {
            setError(result.payload || 'Could not generate insights.');
            setStatus('error');
        }
    }, [dispatch, companyName, organizationId, companyId, ticker]);

    useEffect(() => {
        if (!open) return;
        // Fetch once per open (cached responses return instantly).
        load(false);
        const onKey = (e) => { if (e.key === 'Escape') onClose?.(); };
        document.addEventListener('keydown', onKey);
        const prev = document.body.style.overflow;
        document.body.style.overflow = 'hidden';
        return () => { document.removeEventListener('keydown', onKey); document.body.style.overflow = prev; };
    }, [open, load, onClose]);

    if (!open) return null;

    const insights = data?.insights || {};
    const isEmpty = status === 'done' && !insights.whyHiringNow && !insights.growthOutlook && !(insights.risks?.length);

    return createPortal(
        <div className="fixed inset-0 z-[1000] flex items-center justify-center p-4">
            <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={onClose} />

            <div className="relative w-full max-w-2xl max-h-[88vh] bg-white rounded-2xl shadow-2xl flex flex-col overflow-hidden">
                {/* Header */}
                <div className="flex items-center justify-between px-5 py-4 border-b border-[#EFF1F5] shrink-0">
                    <div className="min-w-0">
                        <div className="flex items-center gap-2">
                            <Sparkles className="w-4 h-4 text-[#3F57E0] shrink-0" />
                            <h2 className="text-[15px] font-extrabold text-[#0F172A] truncate">AI Company Insights</h2>
                        </div>
                        <div className="mt-1 inline-flex items-center gap-1 max-w-full text-[11.5px] font-semibold text-[#475569] bg-[#F1F3F7] px-2 py-0.5 rounded-full">
                            <Building2 className="w-3 h-3 shrink-0" />
                            <span className="truncate">{companyName}</span>
                        </div>
                    </div>
                    <button onClick={onClose} aria-label="Close" className="w-8 h-8 grid place-items-center rounded-lg text-[#64748B] hover:bg-[#F1F3F7] transition-colors shrink-0">
                        <X className="w-4 h-4" />
                    </button>
                </div>

                {/* Body */}
                <div className="overflow-y-auto px-5 py-5">
                    {status === 'loading' && (
                        <div className="flex flex-col items-center justify-center gap-3 py-14 text-center">
                            <Loader2 className="w-7 h-7 text-[#3F57E0] animate-spin" />
                            <p className="text-[13px] font-semibold text-[#0F172A]">Researching {companyName}…</p>
                            <p className="text-[11.5px] text-[#94A3B8] max-w-xs">Pulling fresh signals from the web and analyzing what they mean for you.</p>
                        </div>
                    )}

                    {status === 'error' && (
                        <div className="flex flex-col items-center justify-center gap-3 py-14 text-center">
                            <AlertTriangle className="w-7 h-7 text-amber-500" />
                            <p className="text-[13px] font-semibold text-[#0F172A]">{error}</p>
                            <button onClick={() => load(false)} className="mt-1 text-[12px] font-semibold text-[#3F57E0] hover:underline">Try again</button>
                        </div>
                    )}

                    {status === 'done' && isEmpty && (
                        <div className="py-14 text-center text-[13px] text-[#94A3B8]">No insights available for this company yet.</div>
                    )}

                    {status === 'done' && !isEmpty && (
                        <div className="flex flex-col gap-4">
                            <Section icon={TrendingUp} title="Why they're hiring now" accent="#3F57E0">
                                {insights.whyHiringNow
                                    ? <p className="text-[12.5px] text-[#475569] leading-relaxed">{insights.whyHiringNow}</p>
                                    : <Muted />}
                            </Section>

                            <Section icon={Compass} title="Growth trajectory & outlook" accent="#047857">
                                {insights.growthOutlook
                                    ? <p className="text-[12.5px] text-[#475569] leading-relaxed">{insights.growthOutlook}</p>
                                    : <Muted />}
                            </Section>

                            <Section icon={AlertTriangle} title="Risks & red flags" accent="#D97706">
                                {insights.risks?.length
                                    ? <BulletList items={insights.risks} accent="#D97706" />
                                    : <p className="text-[12.5px] text-[#475569]">No notable risks surfaced in the available data.</p>}
                            </Section>

                            <p className="text-[10.5px] text-[#94A3B8] text-center">
                                {data?.generatedAt && `Updated ${new Date(data.generatedAt).toLocaleDateString()}`}
                            </p>
                        </div>
                    )}
                </div>
            </div>
        </div>,
        document.body
    );
};

const Section = ({ icon: Icon, title, accent, children }) => (
    <div className="rounded-2xl border border-[#E6E8EE] p-4">
        <div className="flex items-center gap-2 mb-2.5">
            <Icon className="w-4 h-4" style={{ color: accent }} />
            <h3 className="text-[12.5px] font-bold text-[#0F172A] uppercase tracking-wide">{title}</h3>
        </div>
        {children}
    </div>
);

const BulletList = ({ items, accent }) => (
    <ul className="flex flex-col gap-2">
        {items.map((t, i) => (
            <li key={i} className="flex gap-2.5 text-[12.5px] text-[#475569] leading-relaxed">
                <span className="mt-1.5 w-1.5 h-1.5 rounded-full shrink-0" style={{ background: accent }} />
                <span>{t}</span>
            </li>
        ))}
    </ul>
);

// `text` overrides the default for sections whose emptiness has a different
// cause — "how to position yourself" can be empty because the user's profile is
// thin, which is not the company's data being insufficient.
const Muted = ({ text }) => <p className="text-[12.5px] text-[#94A3B8]">{text || 'Not enough data to summarize this yet.'}</p>;

export default CompanyInsightsModal;
